import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { INCIDENT_TYPE_META } from "../lib/incidents";
import { toast } from "sonner";
import { AlertOctagon } from "lucide-react";

const TYPES = Object.keys(INCIDENT_TYPE_META);
const SEVERITIES = ["low", "medium", "high", "critical"];

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function IncidentNew() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const now = new Date();
  const [form, setForm] = useState({
    item_id: params.get("vehicle_id") || "",
    incident_type: "damaged",
    severity: "medium",
    title: "",
    description: "",
    start_datetime: toLocalInput(now),
    estimated_resolved_datetime: toLocalInput(new Date(now.getTime() + 24 * 3600 * 1000)),
    update_vehicle_status: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      const list = r.data.filter((v) => v.status !== "retired");
      setVehicles(list);
      if (!form.item_id && list[0]) setForm((f) => ({ ...f, item_id: list[0].id }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        start_datetime: new Date(form.start_datetime).toISOString(),
        estimated_resolved_datetime: new Date(form.estimated_resolved_datetime).toISOString(),
      };
      const { data } = await api.post("/incidents", payload);
      toast.success(`Incident logged · ${data.total_affected} affected booking${data.total_affected === 1 ? "" : "s"}`);
      nav(`/app/incidents/${data.incident.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="incident-new-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
          <AlertOctagon className="w-7 h-7 text-red-600" /> Log an incident
        </h1>
        <p className="text-slate-600 mt-1">We'll auto-find affected bookings and flag them for your review.</p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4" data-testid="incident-form">
        <label className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Vehicle</span>
          <select data-testid="inc-vehicle" required value={form.item_id} onChange={set("item_id")} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.name}{v.plate_number ? ` (${v.plate_number})` : ""}</option>
            ))}
          </select>
        </label>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Incident type</span>
          <select data-testid="inc-type" value={form.incident_type} onChange={set("incident_type")} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
            {TYPES.map((t) => <option key={t} value={t}>{INCIDENT_TYPE_META[t]}</option>)}
          </select>
        </label>
        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Severity</span>
          <select data-testid="inc-severity" value={form.severity} onChange={set("severity")} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white focus:border-[#2D6A4F] outline-none">
            {SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select>
        </label>

        <F label="Short title" required testId="inc-title" value={form.title} onChange={set("title")} className="sm:col-span-2" placeholder="e.g. Toyota Vios: rear bumper damage from minor collision" />

        <F label="Started at" type="datetime-local" required testId="inc-start" value={form.start_datetime} onChange={set("start_datetime")} />
        <F label="Estimated resolved at" type="datetime-local" required testId="inc-end" value={form.estimated_resolved_datetime} onChange={set("estimated_resolved_datetime")} />

        <label className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Notes</span>
          <textarea data-testid="inc-description" value={form.description} onChange={set("description")} rows={3} placeholder="What happened, who reported it, photos location, etc." className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
        </label>

        <label className="sm:col-span-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.update_vehicle_status} onChange={set("update_vehicle_status")} data-testid="inc-update-vehicle" />
          <span>Also update the vehicle's status (recommended for damaged / maintenance / unavailable)</span>
        </label>

        <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => nav(-1)} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
          <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="inc-submit">
            {submitting ? "Logging…" : "Log incident & detect impact"}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testId, className = "", placeholder }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input data-testid={testId} type={type} required={required} value={value} onChange={onChange} placeholder={placeholder}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
    </label>
  );
}
